import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, MapPin } from 'lucide-react';
import { api } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import t from '../translations';

export default function BabyNamesPage() {
  const navigate = useNavigate();
  const { lang } = useLang();
  const tr = t[lang];

  const [birthDate, setBirthDate] = useState('');
  const [birthTime, setBirthTime] = useState('');
  const [cityQuery, setCityQuery]   = useState('');
  const [cities,    setCities]      = useState([]);
  const [searching, setSearching]   = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);
  const [gender,    setGender]      = useState('');
  const [loading,   setLoading]     = useState(false);
  const [error,     setError]       = useState('');
  const [result,    setResult]      = useState(null);

  // City search debounce
  useEffect(() => {
    if (cityQuery.length < 2) { setCities([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(cityQuery)}&format=json&limit=5&addressdetails=1`,
          { headers: { 'Accept-Language': 'en' } }
        );
        const data = await res.json();
        setCities(data.filter(d => d.lat && d.lon).map(d => {
          const a = d.address || {};
          return {
            name: [a.city || a.town || a.village || a.county, a.state, a.country].filter(Boolean).join(', '),
            lat: parseFloat(d.lat),
            lng: parseFloat(d.lon),
          };
        }));
      } catch { /* silent */ }
      setSearching(false);
    }, 450);
    return () => clearTimeout(timer);
  }, [cityQuery]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCity) { setError('Please select a birth city.'); return; }
    if (!gender)       { setError('Please select baby\'s gender.'); return; }
    setError('');
    setLoading(true);
    setResult(null);
    try {
      const data = await api.post('/api/baby-names', {
        birthDate,
        birthTime: birthTime || null,
        birthLat: selectedCity.lat,
        birthLng: selectedCity.lng,
        gender,
      });
      setResult(data);
    } catch (err) {
      setError(err.message || 'Failed to generate names. Please try again.');
    }
    setLoading(false);
  };

  const PLANET_COLORS = {
    Ashwini:'#E8B86D', Bharani:'#C05C5C', Krittika:'#D4A01A', Rohini:'#E87070',
    Mrigashira:'#7DB87D', Ardra:'#6B9DC9', Punarvasu:'#C9A84C', Pushya:'#4A90D9',
    Ashlesha:'#8B6B9B', Magha:'#D4A01A', default: '#C9A84C',
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0A0F1E', paddingBottom: 48 }}>

      {/* Header */}
      <div style={{ padding: '56px 20px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={() => navigate('/home')}
          style={{ background: 'none', border: 'none', color: '#C9A84C', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 0 }}
        >
          <ChevronLeft size={22} />
        </button>
        <div>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, color: '#F5F0E8', fontWeight: 700 }}>
            {tr.babyNames}
          </h1>
          <p style={{ fontSize: 13, color: '#8A8580', marginTop: 2 }}>{tr.babyNamesSubtitle}</p>
        </div>
      </div>

      <div style={{ padding: '0 20px' }}>

        {!result ? (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Birth Date */}
            <div className="card">
              <label style={{ display: 'block', fontSize: 12, color: '#C9A84C', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>
                {tr.babyBirthDate}
              </label>
              <input
                data-testid="baby-birth-date"
                type="date"
                className="input-field"
                value={birthDate}
                onChange={e => setBirthDate(e.target.value)}
                required
                max={new Date().toISOString().split('T')[0]}
              />
            </div>

            {/* Birth Time */}
            <div className="card">
              <label style={{ display: 'block', fontSize: 12, color: '#C9A84C', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>
                {tr.babyBirthTime}
              </label>
              <input
                data-testid="baby-birth-time"
                type="time"
                className="input-field"
                value={birthTime}
                onChange={e => setBirthTime(e.target.value)}
              />
              <p style={{ fontSize: 11, color: '#8A8580', marginTop: 6 }}>More accurate nakshatra with birth time</p>
            </div>

            {/* Birth City */}
            <div className="card">
              <label style={{ display: 'block', fontSize: 12, color: '#C9A84C', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>
                {tr.babyBirthCity}
              </label>
              {selectedCity ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <MapPin size={14} color="#C9A84C" />
                    <p style={{ fontSize: 14, color: '#F5F0E8' }}>{selectedCity.name}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setSelectedCity(null); setCityQuery(''); }}
                    style={{ background: 'none', border: 'none', color: '#C9A84C', cursor: 'pointer', fontSize: 12 }}
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div style={{ position: 'relative' }}>
                  <input
                    data-testid="baby-birth-city"
                    type="text"
                    className="input-field"
                    placeholder="Type birth city…"
                    value={cityQuery}
                    onChange={e => setCityQuery(e.target.value)}
                    autoComplete="off"
                  />
                  {searching && <div style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: '#8A8580', fontSize: 12 }}>↻</div>}
                  {cities.length > 0 && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                      background: '#1a2335', border: '1px solid rgba(201,168,76,0.2)',
                      borderRadius: 12, marginTop: 4, overflow: 'hidden',
                    }}>
                      {cities.map((c, i) => (
                        <button
                          key={i}
                          type="button"
                          onMouseDown={e => { e.preventDefault(); setSelectedCity(c); setCityQuery(''); setCities([]); }}
                          style={{
                            width: '100%', textAlign: 'left', padding: '12px 16px',
                            background: 'transparent', border: 'none',
                            color: '#F5F0E8', cursor: 'pointer', fontSize: 13,
                            borderBottom: i < cities.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                          }}
                        >
                          <MapPin size={12} style={{ display: 'inline', marginRight: 6, color: '#C9A84C' }} />
                          {c.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Gender */}
            <div className="card">
              <label style={{ display: 'block', fontSize: 12, color: '#C9A84C', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>
                {tr.babyGender}
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { value: 'male',   label: tr.male },
                  { value: 'female', label: tr.female },
                  { value: 'other',  label: tr.other.split('/')[0] },
                ].map(g => (
                  <button
                    key={g.value}
                    type="button"
                    data-testid={`baby-gender-${g.value}`}
                    onClick={() => setGender(g.value)}
                    style={{
                      flex: 1, padding: '12px 4px', borderRadius: 10,
                      border: `1px solid ${gender === g.value ? '#C9A84C' : 'rgba(201,168,76,0.15)'}`,
                      background: gender === g.value ? 'rgba(201,168,76,0.1)' : 'transparent',
                      color: gender === g.value ? '#C9A84C' : '#8A8580',
                      cursor: 'pointer', fontSize: 13, fontWeight: 600, transition: 'all 0.2s',
                    }}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
            </div>

            {error && <p style={{ color: '#C0392B', fontSize: 13 }}>{error}</p>}

            <button
              data-testid="generate-names-btn"
              type="submit"
              className="btn-primary"
              disabled={loading || !birthDate || !selectedCity || !gender}
              style={{ fontSize: 15, padding: '16px' }}
            >
              {loading ? tr.generatingNames : tr.generateNames}
            </button>

          </form>
        ) : (
          /* ── Results ── */
          <div className="fade-in-up">

            {/* Nakshatra Card */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(201,168,76,0.12) 0%, rgba(201,168,76,0.04) 100%)',
              border: '1px solid rgba(201,168,76,0.3)',
              borderRadius: 16, padding: '20px', marginBottom: 20, textAlign: 'center',
            }}>
              <p style={{ fontSize: 11, color: '#8A8580', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>
                {tr.nakshatraResult}
              </p>
              <p style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 28, fontWeight: 700,
                color: PLANET_COLORS[result.nakshatra?.nakshatra] || PLANET_COLORS.default,
                marginBottom: 6,
              }}>
                {result.nakshatra?.nakshatra}
              </p>
              <p style={{ fontSize: 13, color: '#8A8580' }}>
                Deity: {result.nakshatra?.deity} · Moon in {result.nakshatra?.moonSign}
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                {result.nakshatra?.syllables?.map((s, i) => (
                  <span key={i} style={{
                    fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 8,
                    background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.2)',
                    color: '#C9A84C', letterSpacing: '0.06em',
                  }}>
                    {s}
                  </span>
                ))}
              </div>
            </div>

            {/* Names Grid */}
            <p style={{ fontSize: 12, color: '#8A8580', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12, fontWeight: 700 }}>
              {tr.suggestedNames}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              {(result.names || []).map((n, i) => (
                <div key={i} style={{
                  background: '#111827', border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: 14, padding: '16px',
                  display: 'flex', alignItems: 'flex-start', gap: 14,
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                    background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: "'Playfair Display', serif", fontWeight: 700,
                    color: '#C9A84C', fontSize: 14,
                  }}>
                    {n.syllable || n.name?.[0]}
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 17, fontWeight: 700, color: '#F5F0E8', fontFamily: "'Playfair Display', serif", marginBottom: 3 }}>
                      {n.name}
                    </p>
                    <p style={{ fontSize: 12, color: '#C9A84C', marginBottom: 2 }}>{n.meaning}</p>
                    {n.origin && <p style={{ fontSize: 11, color: '#8A8580' }}>{n.origin}</p>}
                  </div>
                </div>
              ))}
            </div>

            <button
              data-testid="try-again-btn"
              className="btn-secondary"
              onClick={() => setResult(null)}
            >
              Try Different Details
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
